import { fieldIsRequired, getValidationResult, fieldIsInvalid } from 'shared/utils/tools'
import * as yup from 'yup'
import { getEmptyAddress, nullableAddressSchema } from 'shared/components/AddressAutocomplete/model'
import { LOCALIZATION_CANADA, ORGANIZATION_TYPE_DEFAULT } from 'shared/constants/organizationSetting'
import { tierType } from 'pages/CustomizeScoreboard/model/tier'

export const organizationFeaturesSchema = yup.object().shape({
  program_builder: yup.object().shape({
    is_enabled: yup.boolean(),
  }),
  communication: yup.object().shape({
    is_enabled: yup.boolean(),
    sending_methods: yup.array().of(yup.string().oneOf(['INTERNAL', 'SMS', 'EMAIL'])),
  }),
  report: yup.object().shape({
    is_enabled: yup.boolean(),
    nav: yup.array().of(
      yup.object().shape({
        title: yup.string(),
        tableau_link: yup.string(),
      }),
    ),
    client_id: yup.string().when('is_enabled', {
      is: true,
      then: yup.string().required(fieldIsRequired),
    }),
    user_email: yup.string().when('is_enabled', {
      is: true,
      then: yup.string().required(fieldIsRequired),
    }),
    audience: yup.string().when('is_enabled', {
      is: true,
      then: yup.string().required(fieldIsRequired),
    }),
    scope: yup.string().when('is_enabled', {
      is: true,
      then: yup.string().required(fieldIsRequired),
    }),
    algorithm: yup.string().when('is_enabled', {
      is: true,
      then: yup.string().required(fieldIsRequired),
    }),
    secret_id: yup.string().when('is_enabled', {
      is: true,
      then: yup.string().required(fieldIsRequired),
    }),
    secret_value: yup.string().when('is_enabled', {
      is: true,
      then: yup.string().required(fieldIsRequired),
    }),
  }),
  program: yup.object().shape({
    is_enabled: yup.boolean(),
    program_cycle_start: yup.string().when('is_enabled', {
      is: true,
      then: yup.string().required(fieldIsRequired),
    }),
    program_cycle_end: yup.string().when('is_enabled', {
      is: true,
      then: yup.string().required(fieldIsRequired),
    }),
    program_schedule_start: yup.number(),
    program_schedule_end: yup.number(),
    register_subtitle: yup.string(),
    max_no_of_programs: yup.number().min(0),
    has_saturday: yup.boolean(),
    has_sunday: yup.boolean(),
  }),
  event: yup.object().shape({
    is_enabled: yup.boolean(),
    event_subtitle: yup.string(),
  }),
  document: yup.object().shape({
    is_enabled: yup.boolean(),
  }),
  note: yup.object().shape({
    is_enabled: yup.boolean(),
  }),
  tier: yup.object().shape({
    is_enabled: yup.boolean(),
  }),
  challenge: yup.object().shape({
    is_enabled: yup.boolean(),
  }),
})

export type organizationInformationFeaturesReportNavType = {
  title: string
  tableau_link: string
}

export type organizationInformationFeaturesType = {
  program_builder: {
    is_enabled: boolean
  }
  communication: {
    is_enabled: boolean
    sending_methods: string[]
  }
  report: {
    is_enabled: boolean
    nav: organizationInformationFeaturesReportNavType[]
    client_id?: string
    user_email?: string
    audience: string
    scope: string
    algorithm: string
    secret_id?: string
    secret_value?: string
  }
  program: {
    is_enabled: boolean
    program_cycle_start: string
    program_cycle_end: string
    program_schedule_start: number
    program_schedule_end: number
    register_subtitle: string
    max_no_of_programs: number
    has_saturday: boolean
    has_sunday: boolean
  }
  event: {
    is_enabled: boolean
    event_subtitle: string
  }
  document: {
    is_enabled: boolean
  }
  note: {
    is_enabled: boolean
  }
  tier: {
    is_enabled: boolean
  }
  challenge: {
    is_enabled: boolean
  }
  [k: string]: any | undefined
}

export const organizationInformationSchema = yup.object().shape({
  id: yup.string(),
  name: yup.string().required(fieldIsRequired),
  address: nullableAddressSchema,
  phone_number: yup.string().nullable(),
  email: yup.string().nullable(),
  is_daily_screening_form: yup.boolean(),
  is_member_check_in_or_out: yup.boolean(),
  support_email: yup
    .string()
    .label('Support Email')
    .email(fieldIsInvalid)
    .default(''),
  social_feed: yup.string().nullable(),
  school_option: yup.string().required(fieldIsRequired),
  is_social_feed: yup.boolean(),
  brand_color_hex_code: yup.string().nullable(),
  brand_color_highlight: yup.string().nullable(),
  logo_sign_in_page_url: yup.string().nullable(),
  logo_sign_in_page_alt_text: yup.string().nullable(),
  logo_left_sidebar_url: yup.string().nullable(),
  logo_left_sidebar_alt_text: yup.string().nullable(),
  logo_sign_in_banner_url: yup.string().nullable(),
  logo_sign_in_banner_alt_text: yup.string().nullable(),
  email_banner_url: yup.string().nullable(),
  email_banner_alt_text: yup.string().nullable(),
  store_order_checkout_note: yup.string().nullable(),
  store_order_confirmation_message: yup.string().nullable(),
  localization: yup.string().nullable(),
  type: yup.string().nullable(),
  registration_fields: yup.array().of(yup.string()),
  organization_registration_fields: yup.array().of(yup.string()),
  features: organizationFeaturesSchema,
})

export type organizationInformationType = yup.InferType<typeof organizationInformationSchema> & {
  tiers?: tierType[]
  features: organizationInformationFeaturesType
  [k: string]: any | undefined
}
export function getEmptyOrganizationInformation(): organizationInformationType {
  return {
    id: '',
    name: '',
    address: getEmptyAddress(),
    phone_number: '',
    email: '',
    is_daily_screening_form: false,
    is_member_check_in_or_out: false,
    support_email: '',
    social_feed: '',
    school_option: 'Toronto Disctrict School Board(default)',
    is_social_feed: false,
    brand_color_hex_code: '',
    brand_color_highlight: '',
    logo_sign_in_page_url: '',
    logo_sign_in_page_alt_text: '',
    logo_left_sidebar_url: '',
    logo_left_sidebar_alt_text: '',
    logo_sign_in_banner_url: '',
    logo_sign_in_banner_alt_text: '',
    email_banner_url: '',
    email_banner_alt_text: '',
    store_order_checkout_note: '',
    store_order_confirmation_message: '',
    localization: LOCALIZATION_CANADA,
    type: ORGANIZATION_TYPE_DEFAULT,
    registration_fields: [],
    organization_registration_fields: [],
    tiers: [],
    features: {
      program_builder: {
        is_enabled: false,
      },
      communication: {
        is_enabled: false,
        sending_methods: [],
      },
      report: {
        is_enabled: false,
        nav: [
          {
            title: '',
            tableau_link: '',
          },
          {
            title: '',
            tableau_link: '',
          },
          {
            title: '',
            tableau_link: '',
          },
        ],
        client_id: '',
        user_email: '',
        audience: 'tableau',
        scope: 'tableau:views:embed',
        algorithm: 'HS256',
        secret_id: '',
        secret_value: '',
      },
      program: {
        is_enabled: false,
        program_cycle_start: '',
        program_cycle_end: '',
        program_schedule_start: 6,
        program_schedule_end: 23.5,
        register_subtitle: '',
        max_no_of_programs: 0,
        has_saturday: false,
        has_sunday: false,
      },
      event: {
        is_enabled: false,
        event_subtitle: '',
      },
      document: {
        is_enabled: false,
      },
      note: {
        is_enabled: false,
      },
      tier: {
        is_enabled: false,
      },
      challenge: {
        is_enabled: false,
      },
    },
  }
}

export default function validator(data: organizationInformationType) {
  return getValidationResult(data, organizationInformationSchema)
}
